import AddIcon from '@mui/icons-material/Add'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid2'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  CASE_CLIENT_ROLES,
  CASE_CLIENT_ROLE_LABELS,
  CASE_TYPES,
  CASE_TYPE_LABELS,
  caseInputSchema,
  type CaseDto,
  type CaseInput,
  type ClientDto,
  type TaskTemplateDto,
} from '@lawyer/shared'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import { useAuth } from '../../auth/AuthProvider'
import { useClientOptions, useProjectOptions, useTemplates } from '../../hooks/useLookups'
import { api } from '../../lib/api'
import { formResolver } from '../../lib/form'
import { formatDate } from '../../lib/format'
import { qk } from '../../lib/queryClient'
import { ClientDialog } from '../clients/ClientDialog'
import { ConfirmDialog } from '../common/ConfirmDialog'
import { DateField, DateInput, EntitySelect, EnumSelect, type Option } from '../common/fields'
import { ErrorAlert } from '../common/Feedback'

type FormValues = z.input<typeof caseInputSchema>

const EMPTY: FormValues = {
  projectId: '',
  name: '',
  description: '',
  caseType: 'PROPERTY',
  clientRole: 'SELLER',
  city: '',
  street: '',
  buildingNo: '',
  aptNo: '',
  transactionDate: '',
  feeAmount: '',
}

/** `YYYY-MM-DD` plus N calendar days, in UTC so the day never shifts. */
const addDaysToDay = (day: string, days: number): string => {
  const d = new Date(`${day}T00:00:00.000Z`)
  d.setUTCDate(d.getUTCDate() + days)
  return d.toISOString().slice(0, 10)
}

type Props = {
  open: boolean
  caseRow?: CaseDto | null
  defaults?: Partial<FormValues>
  onClose: () => void
  onCreated?: (created: CaseDto) => void
}

export function CaseDialog({ open, caseRow, defaults, onClose, onCreated }: Props) {
  const queryClient = useQueryClient()
  const projects = useProjectOptions()
  const clientOptions = useClientOptions()
  const templates = useTemplates()
  const { user } = useAuth()
  const isEdit = Boolean(caseRow)

  // Clients joining the case (create only); after creation they are managed on the case card.
  const [clients, setClients] = useState<Option[]>([])
  const [newClientOpen, setNewClientOpen] = useState(false)
  const [confirmDiscard, setConfirmDiscard] = useState(false)

  // Opening tasks from a template (create only)
  const [template, setTemplate] = useState<TaskTemplateDto | null>(null)
  const [dueDays, setDueDays] = useState('')
  const [taskDueDate, setTaskDueDate] = useState('')
  /** Survives an apply failure, so a retry updates the case instead of duplicating it. */
  const createdRef = useRef<CaseDto | null>(null)

  const form = useForm<FormValues, unknown, CaseInput>({
    resolver: formResolver(caseInputSchema),
    defaultValues: EMPTY,
  })

  useEffect(() => {
    if (!open) return
    createdRef.current = null
    setClients([])
    setNewClientOpen(false)
    setConfirmDiscard(false)
    setTemplate(null)
    setDueDays('')
    setTaskDueDate('')
    form.reset(
      caseRow
        ? {
            projectId: caseRow.projectId ?? '',
            name: caseRow.name,
            description: caseRow.description ?? '',
            caseType: caseRow.caseType,
            clientRole: caseRow.clientRole,
            city: caseRow.city ?? '',
            street: caseRow.street ?? '',
            buildingNo: caseRow.buildingNo ?? '',
            aptNo: caseRow.aptNo ?? '',
            transactionDate: caseRow.transactionDate ?? '',
            feeAmount: caseRow.feeAmount ?? '',
          }
        : { ...EMPTY, ...defaults }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, caseRow?.id])

  const transactionDate = form.watch('transactionDate') ?? ''

  // The due date follows "transaction date + N days" until it is edited by hand.
  useEffect(() => {
    const days = Number(dueDays)
    if (dueDays !== '' && transactionDate && Number.isInteger(days) && days >= 0) {
      setTaskDueDate(addDaysToDay(transactionDate, days))
    }
  }, [dueDays, transactionDate])

  const save = useMutation({
    mutationFn: async (values: CaseInput) => {
      if (isEdit) return api.patch<CaseDto>(`/cases/${caseRow?.id}`, values)

      const created = createdRef.current
        ? await api.patch<CaseDto>(`/cases/${createdRef.current.id}`, values)
        : await api.post<CaseDto>('/cases', {
            ...values,
            clients: clients.map((client) => ({ clientId: client.id })),
          })
      createdRef.current = created

      if (template && template.items.length > 0) {
        await api.post(`/task-templates/${template.id}/apply`, {
          itemIds: template.items.map((item) => item.id),
          defaultAssigneeId: user?.id ?? '',
          perItemAssignee: {},
          parentTaskName: template.name,
          startDate: '',
          dueDate: taskDueDate,
          clientId: '',
          caseId: created.id,
          projectId: '',
        })
      }
      return created
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: ['cases'] })
      void queryClient.invalidateQueries({ queryKey: ['clients'] })
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      void queryClient.invalidateQueries({ queryKey: qk.myDay })
      if (caseRow) void queryClient.invalidateQueries({ queryKey: qk.case(caseRow.id) })
      createdRef.current = null
      if (!isEdit) onCreated?.(saved)
      onClose()
    },
    // The case may exist even when the template step failed — keep the list honest.
    onError: () => {
      if (createdRef.current) void queryClient.invalidateQueries({ queryKey: ['cases'] })
    },
  })

  const caseType = form.watch('caseType')
  const isWill = caseType === 'WILL'

  const projectOptions: Option[] = (projects.data ?? []).map((project) => ({
    id: project.id,
    label: project.displayName,
  }))

  const clientPickOptions: Option[] = (clientOptions.data ?? []).map((client) => ({
    id: client.id,
    label: client.displayName,
  }))

  // A client created from here is already saved; it stays even if the case is not.
  const addNewClient = (client: ClientDto) => {
    const label = client.hebrewName?.trim() || client.name?.trim() || 'ללא שם'
    setClients((prev) => [...prev, { id: client.id, label }])
  }

  // City and street of a case in a project come from the project — spec §6.
  const projectId = form.watch('projectId')
  const selectedProject = (projects.data ?? []).find((project) => project.id === projectId)
  const projectAddress = selectedProject
    ? [
        [selectedProject.street, selectedProject.city].filter(Boolean).join(', '),
        [
          selectedProject.gush && `גוש ${selectedProject.gush}`,
          selectedProject.parcel && `חלקה ${selectedProject.parcel}`,
        ]
          .filter(Boolean)
          .join(' · '),
      ]
        .filter(Boolean)
        .join(' · ')
    : ''

  const { errors } = form.formState

  // A click outside or Escape must not throw away a half-filled case.
  const hasUnsavedChanges =
    form.formState.isDirty || clients.length > 0 || template !== null || taskDueDate !== ''
  const requestClose = () => {
    if (save.isPending) return
    if (hasUnsavedChanges) setConfirmDiscard(true)
    else onClose()
  }

  return (
    <Dialog open={open} onClose={requestClose} maxWidth="sm" fullWidth>
      <form onSubmit={form.handleSubmit((values) => save.mutate(values))} noValidate>
        <DialogTitle>{isEdit ? 'עריכת תיק' : 'תיק חדש'}</DialogTitle>

        <DialogContent dividers>
          {save.error ? <ErrorAlert error={save.error} /> : null}
          {save.error && createdRef.current ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              התיק "{createdRef.current.name}" כבר נוצר — ניסיון נוסף יעדכן אותו וייצור את המשימות.
            </Alert>
          ) : null}

          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid size={12}>
              <TextField
                label="שם התיק"
                required
                autoFocus
                {...form.register('name')}
                error={Boolean(errors.name)}
                helperText={errors.name?.message}
              />
            </Grid>
            <Grid size={12}>
              <TextField label="תיאור" multiline minRows={2} {...form.register('description')} />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <EnumSelect
                control={form.control}
                name="caseType"
                label="סוג התיק"
                options={CASE_TYPES.map((type) => ({ id: type, label: CASE_TYPE_LABELS[type] }))}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <EnumSelect
                control={form.control}
                name="clientRole"
                label="צד הלקוחות בתיק"
                options={CASE_CLIENT_ROLES.map((role) => ({
                  id: role,
                  label: CASE_CLIENT_ROLE_LABELS[role],
                }))}
                helperText="כל הלקוחות בתיק תמיד באותו צד"
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <DateField control={form.control} name="transactionDate" label="תאריך העסקה" />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                type="number"
                label="שכר טרחה (₪)"
                {...form.register('feeAmount')}
                error={Boolean(errors.feeAmount)}
                helperText={errors.feeAmount?.message ?? 'כמה מגיע למשרד בתיק הזה'}
                slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
              />
            </Grid>
            <Grid size={12}>
              <EntitySelect
                control={form.control}
                name="projectId"
                label="פרויקט"
                options={projectOptions}
                loading={projects.isPending}
                helperText="תיק המשויך לפרויקט יורש ממנו את אנשי הקשר ואת כתובת הנכס"
              />
            </Grid>

            {isWill ? (
              <Grid size={12}>
                <Alert severity="info">בתיק צוואה שדות הכתובת אינם רלוונטיים ולא יישמרו.</Alert>
              </Grid>
            ) : projectId ? (
              <>
                <Grid size={12}>
                  <Alert severity="info">
                    {projectAddress
                      ? `כתובת הנכס מגיעה מהפרויקט: ${projectAddress}`
                      : 'כתובת הנכס מגיעה מהפרויקט — מומלץ למלא אותה בכרטיס הפרויקט.'}
                  </Alert>
                </Grid>
                <Grid size={6}>
                  <TextField label="בניין" {...form.register('buildingNo')} />
                </Grid>
                <Grid size={6}>
                  <TextField label="דירה" {...form.register('aptNo')} />
                </Grid>
              </>
            ) : (
              <>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField label="עיר" {...form.register('city')} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField label="רחוב" {...form.register('street')} />
                </Grid>
                <Grid size={{ xs: 6, sm: 2 }}>
                  <TextField label="בניין" {...form.register('buildingNo')} />
                </Grid>
                <Grid size={{ xs: 6, sm: 2 }}>
                  <TextField label="דירה" {...form.register('aptNo')} />
                </Grid>
              </>
            )}

            {!isEdit ? (
              <>
                <Grid size={12} sx={{ mt: 1 }}>
                  <Typography variant="overline" color="text.secondary">
                    לקוחות בתיק
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <Autocomplete
                      multiple
                      fullWidth
                      options={clientPickOptions}
                      value={clients}
                      loading={clientOptions.isPending}
                      getOptionLabel={(option) => option.label}
                      isOptionEqualToValue={(a, b) => a.id === b.id}
                      onChange={(_event, next) => setClients(next)}
                      noOptionsText="לא נמצאו לקוחות"
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="לקוחות"
                          placeholder={clients.length === 0 ? 'בחירת לקוחות קיימים' : undefined}
                          helperText="אפשר לבחור כמה לקוחות. חברה שדרכה פועל לקוח נבחרת בכרטיס התיק."
                        />
                      )}
                    />
                    <Button
                      startIcon={<AddIcon />}
                      onClick={() => setNewClientOpen(true)}
                      sx={{ whiteSpace: 'nowrap', mt: 1 }}
                    >
                      לקוח חדש
                    </Button>
                  </Stack>
                </Grid>

                <Grid size={12} sx={{ mt: 1 }}>
                  <Typography variant="overline" color="text.secondary">
                    משימות פתיחה מתבנית (אופציונלי)
                  </Typography>
                </Grid>
                <Grid size={12}>
                  <Autocomplete
                    options={templates.data ?? []}
                    value={template}
                    loading={templates.isPending}
                    getOptionLabel={(option) => option.name}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    onChange={(_event, option) => setTemplate(option)}
                    noOptionsText="אין תבניות"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="תבנית משימות"
                        placeholder="ללא"
                        helperText={
                          template
                            ? `ייווצרו ${template.items.length} משימות בתיק, על שם ${user?.name ?? 'העובד הנוכחי'}`
                            : undefined
                        }
                      />
                    )}
                  />
                </Grid>
                {template ? (
                  template.items.length === 0 ? (
                    <Grid size={12}>
                      <Alert severity="warning">בתבנית זו אין פריטים, ולכן לא ייווצרו משימות.</Alert>
                    </Grid>
                  ) : (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          type="number"
                          label="ימים מתאריך העסקה"
                          value={dueDays}
                          onChange={(e) => setDueDays(e.target.value)}
                          disabled={!transactionDate}
                          slotProps={{ htmlInput: { min: 0 } }}
                          helperText={
                            transactionDate
                              ? dueDays && taskDueDate
                                ? `תאריך היעד יהיה ${formatDate(taskDueDate)}`
                                : 'לדוגמה: 30 — חודש אחרי העסקה'
                              : 'יש למלא תאריך עסקה כדי לחשב'
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <DateInput
                          label="תאריך יעד למשימות"
                          value={taskDueDate}
                          onChange={(next) => {
                            setTaskDueDate(next)
                            setDueDays('')
                          }}
                          helperText="אפשר גם לקבוע ידנית, או להשאיר ריק"
                        />
                      </Grid>
                    </>
                  )
                ) : null}
              </>
            ) : null}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={requestClose}>ביטול</Button>
          <Button type="submit" variant="contained" disabled={save.isPending}>
            {save.isPending ? 'שומר…' : 'שמירה'}
          </Button>
        </DialogActions>
      </form>

      {/* A sibling of the form on purpose: React bubbles the nested form's submit through
          the portal, and inside the case form it would submit the case as well. */}
      <ConfirmDialog
        open={confirmDiscard}
        title={isEdit ? 'לסגור בלי לשמור?' : 'לבטל את פתיחת התיק?'}
        message={
          isEdit
            ? 'השינויים שהוזנו בתיק לא נשמרו ויאבדו.'
            : 'הפרטים שהוזנו לתיק החדש לא נשמרו ויאבדו.'
        }
        confirmLabel="סגירה בלי לשמור"
        onConfirm={() => {
          setConfirmDiscard(false)
          onClose()
        }}
        onClose={() => setConfirmDiscard(false)}
      />
      <ClientDialog
        open={newClientOpen}
        onClose={() => setNewClientOpen(false)}
        onCreated={addNewClient}
      />
    </Dialog>
  )
}
